/**
 * TableContextMenu — Right-click context menu for table cells.
 *
 * Appears at cursor position when user right-clicks inside a <td>/<th>.
 * Provides common table operations and cell styling controls.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Editor } from "@tiptap/core";
import { useTranslation } from "react-i18next";
import "./TableContextMenu.css";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TableContextMenuProps {
  editor: Editor;
  position: { x: number; y: number } | null; // null = hidden
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Color palette for cell backgrounds (Google Docs style)
// ---------------------------------------------------------------------------

const BACKGROUND_COLORS = [
  { color: null, label: "noColor" },
  { color: "#FFFFFF", label: "White" },
  { color: "#F3F3F3", label: "Light Gray 3" },
  { color: "#DADCE0", label: "Light Gray 2" },
  { color: "#E8EAED", label: "Light Gray 1" },
  { color: "#D0E8FF", label: "Light Blue" },
  { color: "#D4EDDA", label: "Light Green" },
  { color: "#FFF3CD", label: "Light Yellow" },
  { color: "#FFE0CC", label: "Light Orange" },
  { color: "#F8D7DA", label: "Light Red" },
  { color: "#E8DAEF", label: "Light Purple" },
  { color: "#1B4F72", label: "Dark Blue" },
  { color: "#1E8449", label: "Dark Green" },
  { color: "#7D6608", label: "Dark Yellow" },
  { color: "#922B21", label: "Dark Red" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TableContextMenu: React.FC<TableContextMenuProps> = ({
  editor,
  position,
  onClose,
}) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);

  // Clamp position to viewport so menu doesn't overflow.
  // If the menu would overflow the bottom, show it above the cursor instead.
  const clampedPosition = React.useMemo(() => {
    if (!position) return null;
    const menuWidth = 220;
    const menuHeight = 420;
    const x = Math.max(8, Math.min(position.x, window.innerWidth - menuWidth - 8));
    const wouldOverflowBottom = position.y + menuHeight + 8 > window.innerHeight;
    const y = wouldOverflowBottom
      ? Math.max(8, position.y - menuHeight)
      : Math.max(8, position.y);
    return { x, y };
  }, [position]);

  // Close on Escape
  useEffect(() => {
    if (!position) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [position, onClose]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const runCommand = useCallback(
    (fn: () => void) => {
      fn();
      onClose();
    },
    [onClose]
  );

  const canMerge = editor.can().mergeOrSplit?.() ?? false;

  // Get current cell background
  const currentBackground =
    (editor.getAttributes("tableCell")?.background as string) || null;

  // Get current vertical alignment
  const currentVAlign =
    (editor.getAttributes("tableCell")?.verticalAlign as string) || "top";

  // ---------------------------------------------------------------------------
  // Menu items
  // ---------------------------------------------------------------------------

  if (!position || !clampedPosition) return null;

  return (
    <>
      {/* Overlay to catch click-outside */}
      <div className="table-context-menu-overlay" onClick={onClose} />

      <div
        ref={menuRef}
        className="table-context-menu"
        style={{ left: clampedPosition.x, top: clampedPosition.y }}
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Insert operations */}
        <button
          className="menu-item"
          onClick={() =>
            runCommand(() =>
              editor.chain().focus().addRowBefore().run()
            )
          }
        >
          <span className="menu-item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.insertRowAbove", "Insert row above")}
          </span>
        </button>

        <button
          className="menu-item"
          onClick={() =>
            runCommand(() =>
              editor.chain().focus().addRowAfter().run()
            )
          }
        >
          <span className="menu-item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.insertRowBelow", "Insert row below")}
          </span>
        </button>

        <button
          className="menu-item"
          onClick={() =>
            runCommand(() =>
              editor.chain().focus().addColumnBefore().run()
            )
          }
        >
          <span className="menu-item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.insertColLeft", "Insert column left")}
          </span>
        </button>

        <button
          className="menu-item"
          onClick={() =>
            runCommand(() =>
              editor.chain().focus().addColumnAfter().run()
            )
          }
        >
          <span className="menu-item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.insertColRight", "Insert column right")}
          </span>
        </button>

        <div className="menu-divider" />

        {/* Delete operations */}
        <button
          className="menu-item"
          onClick={() =>
            runCommand(() => editor.chain().focus().deleteRow().run())
          }
        >
          <span className="menu-item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.deleteRow", "Delete row")}
          </span>
        </button>

        <button
          className="menu-item"
          onClick={() =>
            runCommand(() =>
              editor.chain().focus().deleteColumn().run()
            )
          }
        >
          <span className="menu-item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.deleteCol", "Delete column")}
          </span>
        </button>

        <button
          className="menu-item"
          onClick={() =>
            runCommand(() =>
              editor.chain().focus().deleteTable().run()
            )
          }
          style={{ color: "#c62828" }}
        >
          <span className="menu-item-icon" style={{ color: "#c62828" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M8 6V4h8v2M5 6v14a2 2 0 002 2h10a2 2 0 002-2V6" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.deleteTable", "Delete table")}
          </span>
        </button>

        <div className="menu-divider" />

        {/* Merge / Split */}
        <button
          className={`menu-item ${!canMerge ? "disabled" : ""}`}
          disabled={!canMerge}
          onClick={() =>
            runCommand(() =>
              editor.chain().focus().mergeOrSplit().run()
            )
          }
        >
          <span className="menu-item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M12 3v18" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.mergeSplit", "Merge / Split cells")}
          </span>
        </button>

        <button
          className="menu-item"
          onClick={() =>
            runCommand(() =>
              editor.chain().focus().toggleHeaderRow().run()
            )
          }
        >
          <span className="menu-item-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9h18" />
            </svg>
          </span>
          <span className="menu-item-label">
            {t("tiptapEditor.tableMenu.toggleHeaderRow", "Toggle header row")}
          </span>
        </button>

        <div className="menu-divider" />

        {/* Cell Background — submenu */}
        <div
          className="table-context-submenu"
          onMouseEnter={() => setActiveSubmenu("background")}
          onMouseLeave={() => setActiveSubmenu(null)}
        >
          <button className="menu-item">
            <span className="menu-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 15h18" strokeDasharray="2 2" />
              </svg>
            </span>
            <span className="menu-item-label">
              {t("tiptapEditor.tableMenu.cellBackground", "Cell background")}
            </span>
            <span className="menu-item-arrow">&#9654;</span>
          </button>

          {activeSubmenu === "background" && (
            <div className="table-context-submenu-panel">
              <button
                className="table-context-no-color"
                onClick={() =>
                  runCommand(() =>
                    editor
                      .chain()
                      .focus()
                      .updateAttributes("tableCell", { background: null })
                      .run()
                  )
                }
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#c62828"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M4 4l16 16" />
                </svg>
                {t("tiptapEditor.tableMenu.noColor", "No color")}
              </button>
              <div className="menu-divider" />
              <div className="table-context-color-grid">
                {BACKGROUND_COLORS.filter((c) => c.color !== null).map(
                  (item) => (
                    <button
                      key={item.color}
                      className={`table-context-color-swatch ${
                        currentBackground === item.color ? "active" : ""
                      }`}
                      style={{ backgroundColor: item.color! }}
                      title={item.label}
                      onClick={() =>
                        runCommand(() =>
                          editor
                            .chain()
                            .focus()
                            .updateAttributes("tableCell", {
                              background: item.color,
                            })
                            .run()
                        )
                      }
                    />
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Vertical Alignment — submenu */}
        <div
          className="table-context-submenu"
          onMouseEnter={() => setActiveSubmenu("valign")}
          onMouseLeave={() => setActiveSubmenu(null)}
        >
          <button className="menu-item">
            <span className="menu-item-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16M4 20h16M12 8v8" />
                <path d="M8 12l4-4 4 4" />
              </svg>
            </span>
            <span className="menu-item-label">
              {t("tiptapEditor.tableMenu.verticalAlign", "Vertical align")}
            </span>
            <span className="menu-item-arrow">&#9654;</span>
          </button>

          {activeSubmenu === "valign" && (
            <div className="table-context-submenu-panel">
              {(
                [
                  { value: "top", labelKey: "alignTop", label: "Top" },
                  {
                    value: "middle",
                    labelKey: "alignMiddle",
                    label: "Middle",
                  },
                  {
                    value: "bottom",
                    labelKey: "alignBottom",
                    label: "Bottom",
                  },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  className={`menu-item ${
                    currentVAlign === opt.value ? "checked" : ""
                  }`}
                  onClick={() =>
                    runCommand(() =>
                      editor
                        .chain()
                        .focus()
                        .updateAttributes("tableCell", {
                          verticalAlign: opt.value,
                        })
                        .run()
                    )
                  }
                >
                  <span className="menu-item-check">
                    {currentVAlign === opt.value && (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </span>
                  <span className="menu-item-label">
                    {t(
                      `tiptapEditor.tableMenu.${opt.labelKey}`,
                      opt.label
                    )}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TableContextMenu;
